import { AvailableTemplate, OfferStatus, TemplateOffer } from './types';

/**
 * Per-document state for the Send Offer document list (AXHE-4181).
 *
 * A Document Signing stage can hold several customizable documents. Each one is
 * filled in, previewed, approved and sent on its own, and each has its own
 * offer — so the entry point to Send Offer is a list of documents rather than a
 * single form driving whichever template happened to be resolved first.
 *
 * This module is the ONLY place that reads the per-template `offer` payload.
 * Everything downstream works from `OfferDocument`, so a change to that payload
 * is a one-file change here.
 */

/**
 * What the recruiter is looking at for one document. Every value except
 * `not_started` (the payload reported no offer) and `unknown` (the payload
 * didn't report offer state at all, or reported an offer whose status this UI
 * can't read) is the offer's own status — the list deliberately has no display
 * vocabulary of its own to drift from the backend's.
 */
export type DocumentState = 'not_started' | 'unknown' | OfferStatus;

/** Semantic role for the state's colour + icon. Never colour alone. */
export type DocumentTone = 'neutral' | 'progress' | 'success' | 'warning' | 'error';

/**
 * What entering this document would do, or `none` when it must not be entered.
 * `view` is the read-only path: it opens the offer's own preview without the
 * form, so nothing on the way in can mint a second offer. The offer may still
 * wait on an approver or a send, so a `view` row is not always one the
 * candidate has.
 */
export type DocumentAction = 'start' | 'continue' | 'open' | 'view' | 'none';

export interface OfferDocument {
  templateId: string;
  title: string;
  state: DocumentState;
  /** The offer's external id, when one exists and the payload carried it. */
  offerId: string | null;
  updatedAt: string | null;
}

const OFFER_STATUSES: readonly string[] = [
  'draft',
  'pending_approval',
  'approved',
  'ready_to_send',
  'send_failed',
  'sent',
  'completed',
  'canceled',
] satisfies readonly OfferStatus[];

const isOfferStatus = (value: unknown): value is OfferStatus =>
  typeof value === 'string' && OFFER_STATUSES.includes(value);

/**
 * Reads the `offer` an `available_templates` entry carries.
 *
 * Widened rather than typed off the generated client: `api-clients/` is
 * regenerated from the monolith's OpenAPI spec and this key is not in it yet —
 * the same treatment `normalizeOfferPreview` gives `signature_field_placements`,
 * and it keeps the hand-editing out of a generated package.
 *
 * THREE outcomes, not two, and the distinction is the list's safety property
 * (jdmurphy, PR #3317):
 *
 *   `undefined` — the entry carries no `offer` key at all, so this payload does
 *     not report per-template offer state and the UI does not KNOW whether the
 *     document is already with the candidate. Surfaces as `unknown`, which
 *     closes the row. This is the shape the endpoint returns until the backend
 *     half of AXHE-4181 is deployed, and it is exactly when a wrong answer is
 *     most likely: `not_started` would be asserted for every document on the
 *     stage, including ones already sent.
 *   `null` — an explicit `offer: null`. The backend looked and there is no
 *     offer for this template. Surfaces as `not_started`.
 *   `TemplateOffer` — an offer exists. An offer that IS there but whose status
 *     doesn't parse keeps its offer and reports `status: null`, which surfaces
 *     as `unknown` — never as `not_started`.
 *
 * The two mistakes are not symmetric: showing "not started" for a document
 * already sent invites the recruiter to generate and deliver a second legally
 * significant document for the same template, which is the one outcome this
 * flow guards against everywhere else. Absent, unparseable and explicitly-empty
 * therefore cannot share a return value — collapsing "I wasn't told" into
 * "there is none" is what would make the guard report the opposite of the
 * truth, and it would do so silently.
 */
export const readTemplateOffer = (raw: unknown): TemplateOffer | null | undefined => {
  // Not an object → nothing to read a key off, so "not told" rather than "none".
  if (typeof raw !== 'object' || raw === null) return undefined;
  // The key itself is the signal. `in` distinguishes an absent key from a
  // present one holding null, which `?.` and `==` deliberately do not.
  if (!('offer' in raw)) return undefined;

  const offer = (raw as { offer?: unknown }).offer;
  // `{ offer: undefined }` can only come from a hand-built object, and it says
  // the same thing an absent key says.
  if (offer === undefined) return undefined;
  if (offer === null) return null;

  // Anything else present — object, or a scalar the UI can't understand —
  // reports that an offer EXISTS with nothing readable on it, i.e. `unknown`.
  const {
    external_id: externalId,
    status,
    updated_at: updatedAt,
  } = offer as { external_id?: unknown; status?: unknown; updated_at?: unknown };

  return {
    external_id: typeof externalId === 'string' && externalId !== '' ? externalId : null,
    status: isOfferStatus(status) ? status : null,
    updated_at: typeof updatedAt === 'string' && updatedAt !== '' ? updatedAt : null,
  };
};

export const documentState = (offer: TemplateOffer | null | undefined): DocumentState => {
  // `undefined` is "the payload didn't say", `null` is "the payload said none".
  // Only the second is not_started; the first must close the row.
  if (offer === undefined) return 'unknown';
  if (offer === null) return 'not_started';
  return offer.status ?? 'unknown';
};

export const toDocuments = (templates: AvailableTemplate[]): OfferDocument[] =>
  templates.map(template => ({
    templateId: template.id,
    title: template.title,
    state: documentState(template.offer),
    offerId: template.offer?.external_id ?? null,
    updatedAt: template.offer?.updated_at ?? null,
  }));

/**
 * Exhaustive by construction: adding a status to `OfferStatus` fails the build
 * here rather than silently falling through to a neutral "not started" chip.
 */
export const DOCUMENT_TONE: Record<DocumentState, DocumentTone> = {
  not_started: 'neutral',
  draft: 'progress',
  pending_approval: 'progress',
  approved: 'progress',
  ready_to_send: 'progress',
  sent: 'success',
  completed: 'success',
  send_failed: 'error',
  canceled: 'warning',
  unknown: 'warning',
};

/**
 * `sent` opens READ-ONLY (AXHE-4682): the row's View loads the delivered
 * offer's own preview — document, download, and the Cancel offer action —
 * never the form → preview → send flow, which would POST a second offer for a
 * document the candidate already has.
 *
 * `completed` opens read-only for the same reason, and the server refuses that
 * second offer with 409. Every signer has signed, so the view offers the
 * document and its approvals and no write: the server's `can_cancel` and
 * `can_revise` are false for a signed offer.
 *
 * `ready_to_send` opens read-only too: the form would send it back through
 * approval it has already cleared.
 *
 * `pending_approval` and `approved` open read-only for the same reason as
 * `sent`: an approver holds the offer, or its send is queued. The form's
 * Preview would POST a second offer, which the server refuses with 409.
 *
 * `unknown` is closed for the same reason from the other direction: the UI
 * can't tell whether the document is already out, so it must not offer to send
 * one. That covers both ways of not knowing — an offer whose status won't parse
 * and a payload that carries no offer state at all — which is what makes the
 * list safe to ship ahead of the backend rather than only alongside it.
 *
 * `canceled` and `send_failed` stay open — both are recoverable, and re-sending
 * is the intended fix.
 */
export const DOCUMENT_ACTION: Record<DocumentState, DocumentAction> = {
  not_started: 'start',
  draft: 'continue',
  send_failed: 'continue',
  pending_approval: 'view',
  approved: 'view',
  canceled: 'open',
  ready_to_send: 'view',
  sent: 'view',
  completed: 'view',
  unknown: 'none',
};
